import type { ClassLevelBase, DieType } from '@character-creator/types';
import { getClass } from '@character-creator/data';
import { calculateModifier } from './ability-scores';

export type CritStyle = 'standard' | 'perkins';

/**
 * Hit die values
 */
export const HIT_DIE_VALUES: Record<DieType, number> = {
  d4: 4,
  d6: 6,
  d8: 8,
  d10: 10,
  d12: 12,
  d20: 20,
};

/**
 * Get the average roll for a hit die (rounded up per 5e rules)
 */
export function getHitDieAverage(die: DieType): number {
  const max = HIT_DIE_VALUES[die];
  return Math.ceil(max / 2) + 1;
}

/**
 * Calculate maximum hit points for a character
 * First level: max hit die + CON mod
 * Subsequent levels: hit die roll/average + CON mod
 */
export function calculateMaxHitPoints(
  classes: ClassLevelBase[],
  constitutionScore: number,
  useAverage: boolean = true
): number {
  const conMod = calculateModifier(constitutionScore);

  let totalHP = 0;
  let isFirstLevel = true;

  for (const classLevel of classes) {
    const classData = getClass(classLevel.classId);
    if (!classData) continue;

    const hitDie = classData.hitDie;
    const hitDieMax = HIT_DIE_VALUES[hitDie];
    const hitDieAvg = getHitDieAverage(hitDie);

    for (let level = 1; level <= classLevel.level; level++) {
      if (isFirstLevel) {
        // First level always gets max hit die
        totalHP += hitDieMax + conMod;
        isFirstLevel = false;
      } else {
        // Subsequent levels get average or rolled
        totalHP += (useAverage ? hitDieAvg : hitDieMax) + conMod;
      }
    }
  }

  // HP minimum is 1
  return Math.max(1, totalHP);
}

/**
 * Calculate initiative modifier
 */
export function calculateInitiative(
  dexterityScore: number,
  otherBonuses: number = 0
): number {
  return calculateModifier(dexterityScore) + otherBonuses;
}

/**
 * Calculate base armor class (unarmored)
 */
export function calculateBaseArmorClass(dexterityScore: number): number {
  return 10 + calculateModifier(dexterityScore);
}

/**
 * Calculate armor class with armor
 */
export function calculateArmorClass(
  dexterityScore: number,
  baseAC: number,
  addsDexterity: boolean,
  maxDexBonus?: number,
  shieldBonus: number = 0,
  otherBonuses: number = 0
): number {
  let ac = baseAC;

  if (addsDexterity) {
    const dexMod = calculateModifier(dexterityScore);
    const dexBonus = maxDexBonus !== undefined ? Math.min(dexMod, maxDexBonus) : dexMod;
    ac += dexBonus;
  }

  return ac + shieldBonus + otherBonuses;
}

/**
 * Calculate total hit dice for a character
 */
export function calculateTotalHitDice(classes: ClassLevelBase[]): Map<DieType, number> {
  const hitDice = new Map<DieType, number>();

  for (const classLevel of classes) {
    const classData = getClass(classLevel.classId);
    if (!classData) continue;

    const currentCount = hitDice.get(classData.hitDie) || 0;
    hitDice.set(classData.hitDie, currentCount + classLevel.level);
  }

  return hitDice;
}

/**
 * Calculate attack bonus
 */
export function calculateAttackBonus(
  abilityScore: number,
  proficiencyBonus: number,
  isProficient: boolean,
  otherBonuses: number = 0
): number {
  const abilityMod = calculateModifier(abilityScore);
  const profBonus = isProficient ? proficiencyBonus : 0;
  return abilityMod + profBonus + otherBonuses;
}

/**
 * Calculate damage bonus
 */
export function calculateDamageBonus(
  abilityScore: number,
  otherBonuses: number = 0
): number {
  return calculateModifier(abilityScore) + otherBonuses;
}

/**
 * Transform damage notation for a critical hit.
 *
 * Standard: double each dice group count (1d8+3 → 2d8+3).
 * Perkins:  add max dice value per group to the modifier (1d8+3 → 1d8+11).
 *
 * Modifiers are preserved. Unparseable / empty input is returned unchanged.
 */
export function calculateCritDamageNotation(
  damageNotation: string,
  critStyle: CritStyle,
): string {
  if (!damageNotation.trim()) return damageNotation;

  // Match dice groups (NdX) and signed numeric tokens (+N / -N)
  const diceRegex = /(\d+)d(\d+)/g;
  const parts: Array<{ type: 'dice'; count: number; sides: number } | { type: 'mod'; value: number }> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = diceRegex.exec(damageNotation)) !== null) {
    // Capture any modifier text between previous match and this dice group
    const between = damageNotation.slice(lastIndex, match.index).trim();
    if (between) {
      const num = parseInt(between, 10);
      if (!isNaN(num)) parts.push({ type: 'mod', value: num });
    }
    parts.push({ type: 'dice', count: parseInt(match[1], 10), sides: parseInt(match[2], 10) });
    lastIndex = diceRegex.lastIndex;
  }

  // Trailing modifier after last dice group
  const trailing = damageNotation.slice(lastIndex).trim();
  if (trailing) {
    const num = parseInt(trailing, 10);
    if (!isNaN(num)) parts.push({ type: 'mod', value: num });
  }

  // If no dice groups found, return unchanged
  if (!parts.some((p) => p.type === 'dice')) return damageNotation;

  if (critStyle === 'standard') {
    // Double each dice count
    const transformed = parts.map((p) =>
      p.type === 'dice' ? { ...p, count: p.count * 2 } : p,
    );
    return buildNotation(transformed);
  }

  // Perkins: add (count * sides) per dice group to a running modifier bonus
  let perkinsBonus = 0;
  for (const p of parts) {
    if (p.type === 'dice') perkinsBonus += p.count * p.sides;
  }

  // Rebuild: dice unchanged, add bonus to existing modifier
  const existingMod = parts
    .filter((p): p is { type: 'mod'; value: number } => p.type === 'mod')
    .reduce((sum, p) => sum + p.value, 0);
  const finalMod = existingMod + perkinsBonus;

  const diceStr = parts
    .filter((p): p is { type: 'dice'; count: number; sides: number } => p.type === 'dice')
    .map((p) => `${p.count}d${p.sides}`)
    .join('+');

  if (finalMod === 0) return diceStr;
  const sign = finalMod > 0 ? '+' : '';
  return `${diceStr}${sign}${finalMod}`;
}

function buildNotation(
  parts: Array<{ type: 'dice'; count: number; sides: number } | { type: 'mod'; value: number }>,
): string {
  let result = '';
  for (const p of parts) {
    if (p.type === 'dice') {
      if (result) result += '+';
      result += `${p.count}d${p.sides}`;
    } else {
      if (p.value >= 0) {
        result += `+${p.value}`;
      } else {
        result += `${p.value}`;
      }
    }
  }
  return result;
}
